import { useEffect, useState, useCallback } from 'react';
import { UploadFile } from '@/types/source-modal';
import { processFilesForUpload } from '@/utils/upload-source';

export function useSourceUploadModalState(isOpen: boolean) {
    const [files, setFiles] = useState<UploadFile[]>([]);
    const [activeFileId, setActiveFileId] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setFiles((prev) => {
                prev.forEach((file) => {
                    URL.revokeObjectURL(file.previewUrl);
                });
                return [];
            });
            setActiveFileId(null);
        }
    }, [isOpen]);

    useEffect(() => {
        return () => {
            files.forEach((file) => {
                URL.revokeObjectURL(file.previewUrl);
            });
        };
    }, []);

    const activeFile =
        files.find((file) => file.id === activeFileId) || files[0];
    const isMultipleMode = files.length > 1;

    const handleFileSelect = useCallback((fileId: string) => {
        setActiveFileId(fileId);
    }, []);

    const handleFileDelete = useCallback(
        (fileId: string) => {
            setFiles((prev) => {
                const fileToDelete = prev.find((f) => f.id === fileId);
                if (fileToDelete) {
                    URL.revokeObjectURL(fileToDelete.previewUrl);
                }
                const updated = prev.filter((f) => f.id !== fileId);
                if (activeFileId === fileId && updated.length > 0) {
                    setActiveFileId(updated[0].id);
                } else if (updated.length === 0) {
                    setActiveFileId(null);
                }
                return updated;
            });
        },
        [activeFileId],
    );

    const updateActiveFile = useCallback(
        (
            updates:
                | Partial<UploadFile>
                | ((file: UploadFile) => Partial<UploadFile>),
        ) => {
            if (!activeFile) return;
            setFiles((prev) =>
                prev.map((f) => {
                    if (f.id !== activeFile.id) return f;
                    const resolved =
                        typeof updates === 'function' ? updates(f) : updates;
                    return { ...f, ...resolved };
                }),
            );
        },
        [activeFile?.id],
    );

    const handleTrimChange = useCallback(
        (start: number, end: number) =>
            updateActiveFile({ trimStart: start, trimEnd: end }),
        [updateActiveFile],
    );

    const handleFaceRecognitionChange = useCallback(
        (enabled: boolean) => updateActiveFile({ faceRecognition: enabled }),
        [updateActiveFile],
    );

    const handleLicensePlateRecognitionChange = useCallback(
        (enabled: boolean) =>
            updateActiveFile({ licensePlateRecognition: enabled }),
        [updateActiveFile],
    );

    const handleOpticalCharacterRecognitionChange = useCallback(
        (enabled: boolean) =>
            updateActiveFile({ opticalCharacterRecognition: enabled }),
        [updateActiveFile],
    );

    const handleAnalysisFramesPerSecondChange = useCallback(
        (value: number) => updateActiveFile({ analysisFramesPerSecond: value }),
        [updateActiveFile],
    );

    const handleThumbnailsGenerated = useCallback(
        (thumbnails: string[]) => updateActiveFile({ thumbnails }),
        [updateActiveFile],
    );

    const handleVideoDuration = useCallback(
        (duration: number) =>
            updateActiveFile((file) => ({
                duration,
                trimEnd:
                    file.trimEnd === file.duration ? duration : file.trimEnd,
            })),
        [updateActiveFile],
    );

    const handleFileNameChange = useCallback(
        (name: string) => updateActiveFile({ fileName: name }),
        [updateActiveFile],
    );

    const handleFilesAdded = useCallback(
        async (selectedFiles: File[]) => {
            if (selectedFiles.length === 0) return;

            const newFiles = await processFilesForUpload(selectedFiles);

            setFiles((prev) => [...prev, ...newFiles]);
            if (newFiles.length > 0 && !activeFileId) {
                setActiveFileId(newFiles[0].id);
            }
        },
        [activeFileId],
    );

    return {
        files,
        activeFileId,
        activeFile,
        isMultipleMode,
        handleFileSelect,
        handleFileDelete,
        handleFilesAdded,
        handleTrimChange,
        handleVideoDuration,
        handleThumbnailsGenerated,
        handleFaceRecognitionChange,
        handleLicensePlateRecognitionChange,
        handleOpticalCharacterRecognitionChange,
        handleAnalysisFramesPerSecondChange,
        handleFileNameChange,
    };
}
